import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useVeiculos } from '@/hooks/useVeiculos'
import { formatDate, translateStatus, getStatusBadgeClass } from '@/lib/formatters'
import {
    Search, Plus, Car, Eye, Edit, Trash2, ChevronLeft, ChevronRight, User, Loader2, Filter,
} from 'lucide-react'
import { VeiculoDetailDrawer } from '@/components/admin/VeiculoDetailDrawer'

export function VeiculosPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [search, setSearch] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('todos')
    const [page, setPage] = useState(1)
    const [selectedVeiculo, setSelectedVeiculo] = useState<string | null>(null)

    useEffect(() => { setPageTitle('Veículos') }, [setPageTitle])

    const { data: result, isLoading } = useVeiculos({ search, page, pageSize: 10, status: statusFilter === 'todos' ? undefined : statusFilter })

    const veiculos = result?.data || []
    const totalPages = result?.totalPages || 1

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Veículos</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">{result?.total || 0} veículos cadastrados</p>
                </div>
                <button className="btn btn-primary btn-md gap-2">
                    <Plus size={18} /> Novo Veículo
                </button>
            </div>

            {/* Search + Status Filter */}
            <div className="glass-card p-4">
                <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-700/40" />
                        <input
                            type="text" value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1) }}
                            placeholder="Buscar por placa, marca, modelo ou associado..."
                            className="input pl-10"
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Filter size={16} className="text-surface-700/50" />
                        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1) }} className="input w-auto">
                            <option value="todos">Todos os status</option>
                            <option value="ativo">Ativo</option>
                            <option value="nao_ativado">Não Ativado</option>
                            <option value="inadimplente">Inadimplente</option>
                            <option value="cancelado">Cancelado</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="glass-card overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : veiculos.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16">
                        <Car size={40} className="text-surface-700/20 mb-3" />
                        <p className="text-surface-700/60">Nenhum veículo encontrado</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Placa</th>
                                    <th>Marca / Modelo</th>
                                    <th>Ano</th>
                                    <th>Cor</th>
                                    <th className="hidden md:table-cell">Associado</th>
                                    <th className="hidden lg:table-cell">Status</th>
                                    <th className="hidden lg:table-cell">Cadastro</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {veiculos.map((v) => (
                                    <tr key={v.id} className="hover:bg-surface-50 transition-colors">
                                        <td>
                                            <span className="inline-flex items-center gap-2">
                                                <Car size={16} className="text-primary-600" />
                                                <span className="font-mono font-semibold text-surface-900">{v.placa}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <div>
                                                <p className="font-medium text-surface-900">{v.marca}</p>
                                                <p className="text-xs text-surface-700/60">{v.modelo}</p>
                                            </div>
                                        </td>
                                        <td className="text-surface-700">{v.ano || '—'}</td>
                                        <td>
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="w-3 h-3 rounded-full border border-surface-200"
                                                    style={{ backgroundColor: getColorHex(v.cor || '') }} />
                                                {v.cor || '—'}
                                            </span>
                                        </td>
                                        <td className="hidden md:table-cell">
                                            <span className="inline-flex items-center gap-1.5">
                                                <User size={14} className="text-surface-700/40" />
                                                {v.associado_nome || '—'}
                                            </span>
                                        </td>
                                        <td className="hidden lg:table-cell">
                                            <span className={`badge ${getStatusBadgeClass((v as any).status || 'ativo')}`}>
                                                {translateStatus((v as any).status || 'ativo')}
                                            </span>
                                        </td>
                                        <td className="hidden lg:table-cell text-surface-700/60 text-sm">
                                            {formatDate(v.created_at)}
                                        </td>
                                        <td className="text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button onClick={() => setSelectedVeiculo(v.id)} className="p-1.5 rounded-lg hover:bg-primary-50 text-surface-700 hover:text-primary-600 transition-colors" title="Ver">
                                                    <Eye size={16} />
                                                </button>
                                                <button className="p-1.5 rounded-lg hover:bg-primary-50 text-surface-700 hover:text-primary-600 transition-colors" title="Editar">
                                                    <Edit size={16} />
                                                </button>
                                                <button className="p-1.5 rounded-lg hover:bg-danger-50 text-surface-700 hover:text-danger-600 transition-colors" title="Remover">
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-6 py-4 border-t border-surface-100">
                        <p className="text-sm text-surface-700/60">
                            Página {page} de {totalPages} — {result?.total} resultados
                        </p>
                        <div className="flex items-center gap-1">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30 transition-colors">
                                <ChevronLeft size={16} />
                            </button>
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className="p-2 rounded-lg hover:bg-surface-100 disabled:opacity-30 transition-colors">
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Detail Drawer */}
            <VeiculoDetailDrawer
                veiculo={selectedVeiculo ? (veiculos.find(v => v.id === selectedVeiculo) || null) : null}
                onClose={() => setSelectedVeiculo(null)}
            />
        </div>
    )
}

function getColorHex(cor: string): string {
    const map: Record<string, string> = {
        'branco': '#f8f8f8', 'prata': '#c0c0c0', 'preto': '#2a2a2a', 'cinza': '#808080',
        'vermelho': '#dc2626', 'azul': '#2563eb', 'verde': '#16a34a', 'amarelo': '#eab308',
        'laranja': '#ea580c', 'marrom': '#78350f', 'bege': '#d4a373',
    }
    return map[cor.toLowerCase()] || '#d1d5db'
}
